/**
 * The one icon wrapper. Renders an inline `<svg>` from a NAMED glyph in the
 * self-hosted set (`icons.ts`), a raw path, or raw children — size and color
 * from tokens. Never scatter raw `<svg>`/`<i>`/emoji glyphs in an app.
 *
 * `<Icon name="heart" />` is the path to use: the geometry lives in one place,
 * the name is typo-checked at compile time, and each glyph declares whether it
 * is stroked or filled (a stroke-style path painted with `fill` renders as a
 * solid blob — the bug the ad-hoc per-file path constants shipped).
 *
 * The older `path` API still works unchanged, so existing callers are not
 * broken; it defaults to `fill` as it always did. Pass `variant` to override.
 */
import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { cx } from '../internal/cx.ts';
import type { IconGlyph, IconName } from './icons.ts';
import { ICONS } from './icons.ts';

/** Size presets mapped to spacing tokens. */
export type IconSize = 'sm' | 'md' | 'lg' | 'xl';

/** How the geometry is painted. */
export type IconVariant = 'fill' | 'stroke';

const SIZE_VAR: Record<IconSize, string> = {
  sm: 'var(--space-4)',
  md: 'var(--space-5)',
  lg: 'var(--space-6)',
  xl: 'var(--space-8)',
};

export interface IconProps {
  /** Glyph name from the shared set. Wins over `path`; `children` wins over both. */
  readonly name?: IconName;
  /** SVG path `d` to render. Ignored when `name` or `children` is provided. */
  readonly path?: string;
  /** Raw SVG contents (paths, groups) — the escape hatch. */
  readonly children?: JSX.Element;
  /** Accessible name. When omitted the icon is decorative (`aria-hidden`). */
  readonly label?: string;
  /** Size preset. Defaults to `md`. */
  readonly size?: IconSize;
  /**
   * Paint mode. Defaults to the named glyph's own declaration, or `fill` for a
   * raw `path`/`children` (the historical behaviour).
   */
  readonly variant?: IconVariant;
  /** Stroke width for `stroke` glyphs, in viewBox units. Defaults to 2. */
  readonly strokeWidth?: number;
  /** `viewBox`. Defaults to the named glyph's box, else a 24-unit box. */
  readonly viewBox?: string;
  readonly class?: string;
}

export function Icon(props: IconProps): JSX.Element {
  const glyph = (): IconGlyph | undefined =>
    props.name === undefined ? undefined : ICONS[props.name];

  const decorative = (): boolean => props.label === undefined;
  const dimension = (): string => SIZE_VAR[props.size ?? 'md'];
  const d = (): string => glyph()?.path ?? props.path ?? '';
  const viewBox = (): string => props.viewBox ?? glyph()?.viewBox ?? '0 0 24 24';

  const variant = (): IconVariant => {
    if (props.variant) return props.variant;
    const named = glyph();
    if (!named) return 'fill';
    return named.fill ? 'fill' : 'stroke';
  };

  const stroked = (): boolean => variant() === 'stroke';

  return (
    <svg
      class={cx('f4-icon', props.class)}
      viewBox={viewBox()}
      width={dimension()}
      height={dimension()}
      fill={stroked() ? 'none' : 'currentColor'}
      stroke={stroked() ? 'currentColor' : undefined}
      stroke-width={stroked() ? (props.strokeWidth ?? 2) : undefined}
      stroke-linecap={stroked() ? 'round' : undefined}
      stroke-linejoin={stroked() ? 'round' : undefined}
      role={decorative() ? undefined : 'img'}
      aria-hidden={decorative() ? 'true' : undefined}
      aria-label={props.label}
    >
      <Show when={props.children} fallback={<path d={d()} />}>
        {props.children}
      </Show>
    </svg>
  );
}
